import { friendlyErrorMessage } from '@nutians/shared';
import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { getAccountCreationStatus, signUp } from '../auth.js';
import { AuthShell, authButtonClass, authFieldClass } from '../components/AuthShell.js';
import { i18next } from '../i18n.js';

export default function RegisterPage() {
  const { t } = useTranslation('common');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [over18, setOver18] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accountCreationEnabled, setAccountCreationEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    void getAccountCreationStatus().then(setAccountCreationEnabled);
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!over18) {
      setError(t('auth.register.over18Error'));
      return;
    }
    setSubmitting(true);
    // `attestedOver18` is a required additional field on the user (see
    // apps/api/src/auth.ts), typed on the client via inferAdditionalFields.
    const { error: err } = await signUp.email({
      name,
      email,
      password,
      attestedOver18: true,
      attestedOver18At: new Date().toISOString(),
      locale: i18next.language,
    });
    setSubmitting(false);
    if (err) {
      setError(friendlyErrorMessage(err, t('auth.register.error')));
      return;
    }
    // Email verification is required before sign-in (requireEmailVerification).
    setDone(true);
  }

  if (done) {
    return (
      <AuthShell title={t('auth.checkEmail.title')} subtitle={t('auth.checkEmail.body', { email })}>
        {import.meta.env.DEV && (
          <p className="text-xs leading-relaxed text-ink-soft">
            {t('auth.checkEmail.devNote')}{' '}
            <a
              href="http://localhost:8025"
              className="font-medium text-leaf underline hover:text-leaf-deep"
              target="_blank"
              rel="noreferrer"
            >
              localhost:8025
            </a>
          </p>
        )}
        <Link to="/login" className={`${authButtonClass} mt-6 block`}>
          {t('auth.checkEmail.goToSignIn')}
        </Link>
      </AuthShell>
    );
  }

  if (accountCreationEnabled === null) {
    return (
      <AuthShell title={t('auth.register.title')} subtitle={t('auth.register.subtitle')}>
        <p className="text-sm text-ink-soft" role="status">
          {t('loading')}
        </p>
      </AuthShell>
    );
  }

  if (accountCreationEnabled === false) {
    return (
      <AuthShell
        title={t('auth.register.title')}
        subtitle={t('auth.register.subtitle')}
        footer={
          <Link to="/login" className="font-medium text-leaf hover:text-leaf-deep">
            {t('auth.register.signIn')}
          </Link>
        }
      >
        <p
          className="rounded-lg border border-husk bg-husk/40 p-4 text-sm leading-relaxed text-ink"
          role="status"
        >
          {t('auth.register.temporarilyDisabled')}
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t('auth.register.title')}
      subtitle={t('auth.register.subtitle')}
      footer={
        <>
          {t('auth.register.alreadyHaveAccount')}{' '}
          <Link to="/login" className="font-medium text-leaf hover:text-leaf-deep">
            {t('auth.register.signIn')}
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink">{t('auth.register.name')}</span>
          <input
            type="text"
            autoComplete="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={authFieldClass}
          />
        </label>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink">{t('auth.register.email')}</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={authFieldClass}
          />
        </label>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink">{t('auth.register.password')}</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={authFieldClass}
          />
          <span className="text-xs text-ink-soft">{t('auth.register.passwordHint')}</span>
        </label>

        <label className="flex items-start gap-2.5 text-sm text-ink">
          <input
            type="checkbox"
            checked={over18}
            onChange={(e) => setOver18(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-leaf"
          />
          <span>{t('auth.register.over18')}</span>
        </label>

        {error && (
          <p className="text-sm text-clay" role="alert">
            {error}
          </p>
        )}

        <button type="submit" disabled={submitting} className={`${authButtonClass} mt-1`}>
          {submitting ? t('auth.register.submitting') : t('auth.register.submit')}
        </button>
      </form>
    </AuthShell>
  );
}
